import type { TFunction } from "i18next";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import Avatar from "../../../../../parts/avatar";
import SvgIcon from "../../../../../parts/SvgIcon";
import type {
  ClientConfiguration,
  ConfigurationSection,
  ConfigurationSections,
} from "../../../../../types/clientConfig";
import type { Athlete } from "../../../../../types/user";
import getAvatarUrl from "../../../../../utils/get-avatar-url";
import type { ClientConfigWidgetAvailability } from "../../../../athlete-portal/profile/components/ClientConfiguration/utils";
import {
  type AttentionReason,
  type AttentionSection,
  getAttentionReasons,
} from "./helpers/attention";
import {
  enabledTrackingItemKeys,
  isGoalsOrCycleSectionEnabled,
  sectionChipClass,
  templateDisplayLabel,
} from "./helpers/rowDisplay";
import type { ConfigurationSectionsSummary } from "./types";

interface AthleteRowProps {
  athlete: Athlete;
  athleteConfig: ClientConfiguration | null;
  configSections: ConfigurationSections;
  isLoading: boolean;
  athleteIsFromOtherOrganizationCoach: string | undefined;
  skeleton: React.ReactNode;
  clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
  sectionHasData: Record<string, boolean> | null;
}

type SectionModeSummaryKey =
  | "nutrition"
  | "mealPlans"
  | "dailyAccountabilities";

function summarizeSectionModeForTable(
  t: TFunction<"athlete-portal">,
  sectionKey: SectionModeSummaryKey,
  section: ConfigurationSection | undefined
): string {
  if (section?.mode === "hidden" && section.clientOptIn) {
    return t(
      `clientConfiguration.configList.sectionModes.${sectionKey}.clientOptIn`
    );
  }
  const mode = section?.mode ?? "hidden";
  return t(`clientConfiguration.configList.sectionModes.${sectionKey}.${mode}`);
}

const TRACKING_ICONS = {
  readiness: "/img/new-icons/tracking-items/readiness-rate.svg",
  sessionRpe: "/img/new-icons/tracking-items/session-rpe.svg",
  bodyWeight: "/img/new-icons/tracking-items/bodyweight.svg",
  bodyFat: "/img/new-icons/tracking-items/body-fat.svg",
  bodyMeasurements: "/img/new-icons/tracking-items/body-measurements.svg",
  progressPhotos: "/img/new-icons/tracking-items/progress-photos.svg",
  mealPhotos: "/img/new-icons/tracking-items/meal-photos.svg",
  sleep: "/img/new-icons/tracking-items/sleep.svg",
  steps: "/img/new-icons/tracking-items/steps.svg",
  water: "/img/new-icons/tracking-items/water-intake.svg",
  hrv: "/img/new-icons/heart-pulse-solid.svg",
  restingHeartRate: "/img/new-icons/heart-solid.svg",
};

function summarizeConfig(
  t: TFunction<"athlete-portal">,
  config: ClientConfiguration | null
): ConfigurationSectionsSummary | null {
  if (!config) return null;
  const configSections = config.sections ?? {};

  return {
    templateLabel: templateDisplayLabel(config),
    trackingItems: enabledTrackingItemKeys(configSections.tracking),
    trainingSet: isGoalsOrCycleSectionEnabled(configSections.training),
    nutritionMode: summarizeSectionModeForTable(
      t,
      "nutrition",
      configSections.nutrition
    ),
    mealPlansMode: summarizeSectionModeForTable(
      t,
      "mealPlans",
      configSections.mealPlans
    ),
    dailyAccountabilitiesMode: summarizeSectionModeForTable(
      t,
      "dailyAccountabilities",
      configSections.dailyAccountabilities
    ),
    goalsSet: isGoalsOrCycleSectionEnabled(configSections.goals),
    femaleCycleTrackingSet: isGoalsOrCycleSectionEnabled(
      configSections.femaleCycleTracking
    ),
  };
}

/** The "Needs attention" chip a cell shows instead of its normal value. */
const AttentionChip = ({
  section,
  reason,
  t,
}: {
  section: AttentionSection;
  reason: AttentionReason;
  t: TFunction<"athlete-portal">;
}) => (
  <span
    className="chip warning"
    data-tooltip-id="widget-warning-tooltip"
    data-tooltip-content={
      reason === "clientNoWidget"
        ? t(
            `clientConfiguration.widgetRequired.activeClientNoWidget.${
              section as "mealPlans" | "dailyAccountabilities"
            }`
          )
        : t(`clientConfiguration.coachNoData.${section}`)
    }
  >
    {t("clientConfiguration.widgetRequired.needsAttention")}
  </span>
);

export const AthleteRow = memo(
  ({
    athlete,
    athleteConfig,
    configSections,
    isLoading,
    athleteIsFromOtherOrganizationCoach,
    skeleton,
    clientConfigWidgetAvailability,
    sectionHasData,
  }: AthleteRowProps) => {
    const { t } = useTranslation("athlete-portal");
    const summary = summarizeConfig(t, athleteConfig);
    // sectionHasData is null when the batch validator hasn't loaded yet.
    const attention = getAttentionReasons(
      configSections,
      sectionHasData,
      clientConfigWidgetAvailability
    );
    const attentionChip = (section: AttentionSection) => {
      const reason = attention[section];
      return reason ? (
        <AttentionChip section={section} reason={reason} t={t} />
      ) : null;
    };

    return (
      <tr
        key={athlete?._id || athlete.email || athlete.name}
        data-unloaded-client-id={
          isLoading && athlete?._id ? String(athlete._id) : undefined
        }
        style={{ cursor: "pointer" }}
        onClick={() => {
          if (athlete?._id) {
            window.location.href = athleteIsFromOtherOrganizationCoach
              ? `/athlete-portal/profile/?athlete=${athlete._id}&section=client-configuration&organizationCoach=${athleteIsFromOtherOrganizationCoach}`
              : `/athlete-portal/profile/?athlete=${athlete._id}&section=client-configuration`;
          }
        }}
      >
        <td className="avatar-cell">
          <div>
            <Avatar
              size={32}
              url={getAvatarUrl(athlete, false, "dark", false)}
              user={athlete}
              overlay={null}
              useBgImg={false}
              height={null}
              unit={null}
              className={null}
            />
            {isLoading ? (
              skeleton
            ) : (
              <span>{athlete.name || athlete.email || "—"}</span>
            )}
          </div>
        </td>
        <td>
          {isLoading ? skeleton : <span>{summary?.templateLabel || "—"}</span>}
        </td>
        <td className="training-cell">
          {isLoading
            ? skeleton
            : (attentionChip("training") ?? (
                <span
                  className={`label ${summary?.trainingSet ? "on" : "off"}`}
                >
                  {summary?.trainingSet && (
                    <SvgIcon src="/img/new-icons/check.svg" />
                  )}
                  {summary?.trainingSet
                    ? t("clientConfiguration.configList.trainingOn")
                    : t("clientConfiguration.configList.trainingOff")}
                </span>
              ))}
        </td>
        <td>
          {isLoading
            ? skeleton
            : (attentionChip("nutrition") ?? (
                <span
                  className={`chip ${sectionChipClass(
                    configSections.nutrition
                  )}`}
                >
                  {summary?.nutritionMode || "—"}
                </span>
              ))}
        </td>
        <td>
          {isLoading
            ? skeleton
            : (attentionChip("mealPlans") ?? (
                <span
                  className={`chip ${sectionChipClass(
                    configSections.mealPlans
                  )}`}
                >
                  {summary?.mealPlansMode || "—"}
                </span>
              ))}
        </td>
        <td>
          {isLoading
            ? skeleton
            : (attentionChip("dailyAccountabilities") ?? (
                <span
                  className={`chip ${sectionChipClass(
                    configSections.dailyAccountabilities
                  )}`}
                >
                  {summary?.dailyAccountabilitiesMode || "—"}
                </span>
              ))}
        </td>
        <td className="goals-cell">
          {isLoading
            ? skeleton
            : (attentionChip("goals") ?? (
                <span className={`label ${summary?.goalsSet ? "on" : "off"}`}>
                  {summary?.goalsSet && (
                    <SvgIcon src="/img/new-icons/check.svg" />
                  )}
                  {summary?.goalsSet
                    ? t("clientConfiguration.configList.goalsOn")
                    : t("clientConfiguration.configList.goalsOff")}
                </span>
              ))}
        </td>
        <td className="tracking-items-cell">
          {isLoading ? (
            skeleton
          ) : (
            <div>
              {summary?.trackingItems?.map((item) => (
                <span
                  key={item}
                  data-tooltip-id="tracking-emoji-tooltip"
                  data-tooltip-content={t(
                    `clientConfiguration.configList.trackingLabels.${item}`
                  )}
                >
                  <SvgIcon src={TRACKING_ICONS[item]} />
                </span>
              ))}
            </div>
          )}
        </td>
        <td className="female-cycle-tracking-cell">
          {isLoading ? (
            skeleton
          ) : (
            <span
              className={`label ${
                summary?.femaleCycleTrackingSet ? "on" : "off"
              }`}
            >
              {summary?.femaleCycleTrackingSet && (
                <SvgIcon src="/img/new-icons/check.svg" />
              )}
              {summary?.femaleCycleTrackingSet
                ? t("clientConfiguration.configList.cycleOn")
                : t("clientConfiguration.configList.cycleOff")}
            </span>
          )}
        </td>
      </tr>
    );
  }
);
